import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Brain, Bell, Palette, Save, Key, CalendarDays, Heart, MessageSquare, HardDrive, Activity } from 'lucide-react';
import { toast } from 'sonner';

const AI_MODELS = {
  openai: [
    { value: 'gpt-5.2', label: 'GPT-5.2' },
    { value: 'gpt-5.1', label: 'GPT-5.1' },
    { value: 'gpt-5', label: 'GPT-5' },
    { value: 'gpt-4o', label: 'GPT-4o' },
    { value: 'gpt-4.1', label: 'GPT-4.1' },
  ],
  anthropic: [
    { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5' },
    { value: 'claude-opus-4-5-20251101', label: 'Claude Opus 4.5' },
    { value: 'claude-4-sonnet-20250514', label: 'Claude 4 Sonnet' },
    { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
  ],
};

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [aiConfig, setAiConfig] = useState({ provider: 'openai', model: 'gpt-5.2', temperature: 0.3, max_tokens: 2000 });
  const [aiKeys, setAiKeys] = useState({ openai_key: '', anthropic_key: '' });
  const [notifications, setNotifications] = useState({ email: true, sms: false, in_app: true });
  const [branding, setBranding] = useState({ primary_color: '#0284c7', clinic_name: '', logo_url: '' });
  const [appointmentMode, setAppointmentMode] = useState('request');
  const [showSummary, setShowSummary] = useState(true);
  const [storageType, setStorageType] = useState('local');
  const [s3Config, setS3Config] = useState({ bucket: '', region: '', access_key: '', secret_key: '' });
  const [followupConfig, setFollowupConfig] = useState({ enabled: false, day_1: true, week_1: true, channel: 'email' });
  const [communication, setCommunication] = useState({ twilio_sid: '', twilio_auth_token: '', twilio_phone: '', twilio_whatsapp: '', smtp_host: '', smtp_port: '587', smtp_user: '', smtp_password: '', smtp_from: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get('/settings').then(res => {
      const d = res.data;
      setSettings(d);
      if (d.ai_config) setAiConfig(d.ai_config);
      if (d.ai_keys) setAiKeys(d.ai_keys);
      if (d.notifications) setNotifications(d.notifications);
      if (d.branding) setBranding(d.branding);
      if (d.appointment_mode) setAppointmentMode(d.appointment_mode);
      if (d.show_triage_summary_to_patient !== undefined) setShowSummary(d.show_triage_summary_to_patient);
      if (d.storage_type) setStorageType(d.storage_type);
      if (d.s3_config) setS3Config(d.s3_config);
      if (d.followup_config) setFollowupConfig(d.followup_config);
      if (d.communication) setCommunication(d.communication);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const saveAiConfig = async () => {
    setSaving(true);
    try {
      await API.put('/settings/ai-config', aiConfig);
      await API.put('/settings', { ai_keys: aiKeys });
      toast.success('AI configuration saved');
    } catch (err) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const saveAllSettings = async () => {
    setSaving(true);
    try {
      await API.put('/settings', {
        notifications, branding, appointment_mode: appointmentMode,
        show_triage_summary_to_patient: showSummary,
        storage_type: storageType, s3_config: s3Config,
        followup_config: followupConfig, communication
      });
      toast.success('Settings saved');
    } catch (err) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Activity className="w-8 h-8 text-sky-600 animate-pulse-subtle" /></div>;

  const models = AI_MODELS[aiConfig.provider] || [];

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-[900px]">
      <div>
        <h1 data-testid="settings-title" className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Settings</h1>
        <p className="text-slate-500 mt-1">Configure your workspace preferences</p>
      </div>

      <Tabs defaultValue="ai" className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="ai"><Brain className="w-4 h-4 mr-1.5" /> AI Config</TabsTrigger>
          <TabsTrigger value="appointments"><CalendarDays className="w-4 h-4 mr-1.5" /> Appointments</TabsTrigger>
          <TabsTrigger value="followups"><Heart className="w-4 h-4 mr-1.5" /> Follow-Ups</TabsTrigger>
          <TabsTrigger value="communication"><MessageSquare className="w-4 h-4 mr-1.5" /> Communication</TabsTrigger>
          <TabsTrigger value="general"><Palette className="w-4 h-4 mr-1.5" /> General</TabsTrigger>
        </TabsList>

        <TabsContent value="ai" className="space-y-6">
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center"><Brain className="w-5 h-5 text-sky-600" /></div>
                <div><CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>AI Model Configuration</CardTitle><p className="text-sm text-slate-500 mt-0.5">Choose the AI model for triage engine</p></div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Provider</Label>
                  <Select value={aiConfig.provider} onValueChange={v => setAiConfig({ ...aiConfig, provider: v, model: AI_MODELS[v]?.[0]?.value || '' })}>
                    <SelectTrigger data-testid="ai-provider-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openai">OpenAI</SelectItem>
                      <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Model</Label>
                  <Select value={aiConfig.model} onValueChange={v => setAiConfig({ ...aiConfig, model: v })}>
                    <SelectTrigger data-testid="ai-model-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{models.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between"><Label>Temperature</Label><span className="text-sm font-mono text-slate-600">{aiConfig.temperature.toFixed(2)}</span></div>
                <Slider data-testid="ai-temperature-slider" value={[aiConfig.temperature]} onValueChange={([v]) => setAiConfig({ ...aiConfig, temperature: v })} min={0} max={1} step={0.05} />
              </div>
              <div className="space-y-2"><Label>Max Tokens</Label><Input data-testid="ai-max-tokens" type="number" value={aiConfig.max_tokens} onChange={e => setAiConfig({ ...aiConfig, max_tokens: parseInt(e.target.value) || 2000 })} className="max-w-xs" /></div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center"><Key className="w-5 h-5 text-amber-600" /></div>
                <div><CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>API Keys</CardTitle><p className="text-sm text-slate-500 mt-0.5">Set custom API keys (leave empty to use Emergent Universal Key)</p></div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>OpenAI API Key</Label>
                <Input data-testid="openai-key" type="password" value={aiKeys.openai_key || ''} onChange={e => setAiKeys({ ...aiKeys, openai_key: e.target.value })} placeholder="sk-..." />
              </div>
              <div className="space-y-2">
                <Label>Anthropic API Key</Label>
                <Input data-testid="anthropic-key" type="password" value={aiKeys.anthropic_key || ''} onChange={e => setAiKeys({ ...aiKeys, anthropic_key: e.target.value })} placeholder="sk-ant-..." />
              </div>
              <p className="text-xs text-slate-400">If left empty, the platform will use the Emergent Universal Key.</p>
            </CardContent>
          </Card>

          <div className="flex items-center justify-between py-2 px-1">
            <div><p className="text-sm font-medium">Show Triage Summary to Patient</p><p className="text-xs text-slate-500">Display collected information summary after triage completion</p></div>
            <Switch checked={showSummary} onCheckedChange={setShowSummary} data-testid="show-summary-toggle" />
          </div>

          <Button data-testid="save-ai-config" onClick={saveAiConfig} disabled={saving} className="bg-sky-600 hover:bg-sky-700"><Save className="w-4 h-4 mr-2" /> Save AI Config</Button>
        </TabsContent>

        <TabsContent value="appointments">
          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Appointment Booking Mode</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Select value={appointmentMode} onValueChange={setAppointmentMode}>
                <SelectTrigger data-testid="appointment-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="request">Request Mode - Patient requests, clinic confirms via phone</SelectItem>
                  <SelectItem value="system">System Mode - Auto-find next available time slot</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-slate-500">{appointmentMode === 'request' ? 'Patients submit appointment requests. Your team will call them back with available times.' : 'The system automatically finds the next available slot based on doctor schedules.'}</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="followups" className="space-y-6">
          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Follow-Up Configuration</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-medium">Enable Auto Follow-Ups</p><p className="text-xs text-slate-500">Automatically create follow-ups when appointments are completed</p></div>
                <Switch checked={followupConfig.enabled} onCheckedChange={v => setFollowupConfig({ ...followupConfig, enabled: v })} data-testid="followup-enabled" />
              </div>
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-medium">1 Day After</p><p className="text-xs text-slate-500">Send check-in message 1 day after appointment</p></div>
                <Switch checked={followupConfig.day_1} onCheckedChange={v => setFollowupConfig({ ...followupConfig, day_1: v })} />
              </div>
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-medium">1 Week After</p><p className="text-xs text-slate-500">Send well-being check 1 week after appointment</p></div>
                <Switch checked={followupConfig.week_1} onCheckedChange={v => setFollowupConfig({ ...followupConfig, week_1: v })} />
              </div>
              <div className="space-y-2">
                <Label>Default Channel</Label>
                <Select value={followupConfig.channel} onValueChange={v => setFollowupConfig({ ...followupConfig, channel: v })}>
                  <SelectTrigger data-testid="followup-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="sms">SMS (Twilio)</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp (Twilio)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="communication" className="space-y-6">
          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Twilio (SMS & WhatsApp)</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Account SID</Label><Input data-testid="twilio-sid" type="password" value={communication.twilio_sid || ''} onChange={e => setCommunication({ ...communication, twilio_sid: e.target.value })} placeholder="ACxxxxxxxx" /></div>
                <div className="space-y-2"><Label>Auth Token</Label><Input data-testid="twilio-token" type="password" value={communication.twilio_auth_token || ''} onChange={e => setCommunication({ ...communication, twilio_auth_token: e.target.value })} placeholder="xxxxxxxxxx" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>SMS Phone Number</Label><Input value={communication.twilio_phone || ''} onChange={e => setCommunication({ ...communication, twilio_phone: e.target.value })} placeholder="+1234567890" /></div>
                <div className="space-y-2"><Label>WhatsApp Number</Label><Input value={communication.twilio_whatsapp || ''} onChange={e => setCommunication({ ...communication, twilio_whatsapp: e.target.value })} placeholder="+1234567890" /></div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Email (SMTP)</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>SMTP Host</Label><Input value={communication.smtp_host || ''} onChange={e => setCommunication({ ...communication, smtp_host: e.target.value })} placeholder="smtp.gmail.com" /></div>
                <div className="space-y-2"><Label>SMTP Port</Label><Input value={communication.smtp_port || '587'} onChange={e => setCommunication({ ...communication, smtp_port: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Username</Label><Input value={communication.smtp_user || ''} onChange={e => setCommunication({ ...communication, smtp_user: e.target.value })} placeholder="email@domain.com" /></div>
                <div className="space-y-2"><Label>Password</Label><Input type="password" value={communication.smtp_password || ''} onChange={e => setCommunication({ ...communication, smtp_password: e.target.value })} /></div>
              </div>
              <div className="space-y-2"><Label>From Address</Label><Input value={communication.smtp_from || ''} onChange={e => setCommunication({ ...communication, smtp_from: e.target.value })} placeholder="noreply@meddivo.com" /></div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="general" className="space-y-6">
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><Bell className="w-5 h-5 text-emerald-600" /></div>
                <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Notifications</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {[{ key: 'email', label: 'Email Notifications' }, { key: 'sms', label: 'SMS Notifications' }, { key: 'in_app', label: 'In-App Notifications' }].map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between py-2">
                  <p className="text-sm font-medium text-slate-900">{label}</p>
                  <Switch data-testid={`notification-${key}`} checked={notifications[key]} onCheckedChange={v => setNotifications({ ...notifications, [key]: v })} />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Branding</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Clinic Name</Label><Input value={branding.clinic_name} onChange={e => setBranding({ ...branding, clinic_name: e.target.value })} /></div>
                <div className="space-y-2">
                  <Label>Primary Color</Label>
                  <div className="flex gap-2">
                    <input type="color" value={branding.primary_color} onChange={e => setBranding({ ...branding, primary_color: e.target.value })} className="w-10 h-10 rounded border cursor-pointer" />
                    <Input value={branding.primary_color} onChange={e => setBranding({ ...branding, primary_color: e.target.value })} className="flex-1" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center"><HardDrive className="w-5 h-5 text-indigo-600" /></div>
                <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Storage</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select value={storageType} onValueChange={setStorageType}>
                <SelectTrigger data-testid="storage-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="local">Local Storage (Default)</SelectItem>
                  <SelectItem value="s3">Amazon S3</SelectItem>
                </SelectContent>
              </Select>
              {storageType === 's3' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Bucket</Label><Input value={s3Config.bucket} onChange={e => setS3Config({ ...s3Config, bucket: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Region</Label><Input value={s3Config.region} onChange={e => setS3Config({ ...s3Config, region: e.target.value })} placeholder="us-east-1" /></div>
                  <div className="space-y-2"><Label>Access Key</Label><Input type="password" value={s3Config.access_key} onChange={e => setS3Config({ ...s3Config, access_key: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Secret Key</Label><Input type="password" value={s3Config.secret_key} onChange={e => setS3Config({ ...s3Config, secret_key: e.target.value })} /></div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Button data-testid="save-all-settings" onClick={saveAllSettings} disabled={saving} className="bg-sky-600 hover:bg-sky-700"><Save className="w-4 h-4 mr-2" /> Save All Settings</Button>
    </div>
  );
}
